import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { TargetType, VariableType, KPIResult } from '@/lib/calculator';
import { addMonths } from 'date-fns';

export type ViewMode = 'snapshot' | 'compare' | 'power';
export type StepDirection = 'both' | 'up' | 'down';

interface Settings {
  amortizationYears: number;
  debtServiceRatio: number; // Added debt service ratio setting
  downPaymentPercentage: number; // Added down payment percentage setting
}

interface Variable {
  type: VariableType;
  value: number;
  step: number;
  stepDirection: StepDirection; // New property for step direction
  label: string;
}

interface CalculatorState {
  target: TargetType;
  viewMode: ViewMode;
  rowVariables: VariableType[];
  columnVariables: VariableType[];
  values: Record<VariableType, Variable>;
  settings: Settings;
  selectedCell: { row: number; col: number } | null;
  results: KPIResult[][];
  showToast: boolean;
  toastMessage: string;
  setTarget: (target: TargetType) => void;
  setViewMode: (mode: ViewMode) => void;
  updateSetting: <K extends keyof Settings>(key: K, value: Settings[K]) => void;
  updateValue: (type: VariableType, value: number) => void;
  updateStep: (type: VariableType, step: number) => void;
  updateStepDirection: (type: VariableType, direction: StepDirection) => void; // New action
  addRowVariable: (type: VariableType) => void;
  removeRowVariable: (index: number) => void;
  addColumnVariable: (type: VariableType) => void;
  removeColumnVariable: (index: number) => void;
  setSelectedCell: (cell: { row: number; col: number } | null) => void;
  setResults: (results: KPIResult[][]) => void;
  clearVariables: () => void;
  resetToDefaults: () => void;
  resetStepsToDefaults: () => void;
  hideToast: () => void;
}

const DEFAULT_SETTINGS: Settings = {
  amortizationYears: 30,
  debtServiceRatio: 0.32, // Default to 32%
  downPaymentPercentage: 0.20 // Default to 20%
};

const now = new Date();
// Set default date to the first day of next month
const nextMonthFirstDay = addMonths(new Date(now.getFullYear(), now.getMonth(), 1), 1).getTime();
const YEAR_IN_MS = 365 * 24 * 60 * 60 * 1000;

export const VARIABLE_ORDER: VariableType[] = [
  'price',
  'date',
  'income',
  'downPayment',
  'savings',
  'interest'
];

const DEFAULT_VALUES: Record<VariableType, Variable> = {
  price: { type: 'price', value: 500000, step: 50000, stepDirection: 'both', label: 'Purchase Price' },
  date: { type: 'date', value: nextMonthFirstDay, step: YEAR_IN_MS, stepDirection: 'up', label: 'Purchase Date' },
  income: { type: 'income', value: 85000, step: 10000, stepDirection: 'up', label: 'Annual Income' },
  downPayment: { type: 'downPayment', value: 100000, step: 10000, stepDirection: 'up', label: 'Down Payment' },
  savings: { type: 'savings', value: 500, step: 500, stepDirection: 'up', label: 'Additional Monthly Savings For Down Payment' },
  interest: { type: 'interest', value: 6.0, step: 0.5, stepDirection: 'both', label: 'Mortgage Interest Rate' }
};

const DEFAULT_STEPS: Record<VariableType, number> = {
  price: 50000,
  date: YEAR_IN_MS,
  income: 10000,
  downPayment: 10000,
  savings: 500,
  interest: 0.5
};

// Function to calculate grid dimensions and steps based on direction
const getGridDimensions = (rowVars: VariableType[], colVars: VariableType[]) => ({
  rows: rowVars.length > 0 ? 3 : 0,
  cols: colVars.length > 0 ? 3 : 1
});

// Function to get middle cell accounting for step direction
const getMiddleCell = (rows: number, cols: number) => ({
  row: Math.floor(rows / 2),
  col: Math.floor(cols / 2)
});

const getRequiredVariablesForTarget = (target: TargetType): VariableType[] => {
  switch (target) {
    case 'price':
      return ['income', 'date'];
    case 'date':
    case 'savings':
      return ['income', 'price'];
    default:
      return [];
  }
};

const STORE_VERSION = 29; // Incremented to handle updated down payment default ($100,000)

export const useCalculatorStore = create<CalculatorState>()(
  persist(
    (set, get) => ({
      // Default to price target and snapshot view mode on each page load/refresh
      target: 'price',
      viewMode: 'snapshot',
      rowVariables: ['income', 'date'],
      columnVariables: [],
      values: DEFAULT_VALUES,
      settings: DEFAULT_SETTINGS,
      selectedCell: { row: 1, col: 0 },
      results: [],
      showToast: false,
      toastMessage: '',
      
      setViewMode: (viewMode) => set({ viewMode }),
      
      setTarget: (target) => {
        const state = get();
        const requiredVars = getRequiredVariablesForTarget(target);
        
        if (state.rowVariables.length === 0 && state.columnVariables.length === 0) {
          const { rows, cols } = getGridDimensions(requiredVars, []);
          setTimeout(() => {
            set({
              target,
              rowVariables: requiredVars,
              columnVariables: [],
              selectedCell: rows > 0 ? getMiddleCell(rows, cols) : null,
              results: [],
              viewMode: 'snapshot' // Always reset to snapshot mode
            });
          }, 150);
          return;
        }

        let rowVariables = state.rowVariables.filter(v => v !== target);
        let columnVariables = state.columnVariables.filter(v => v !== target);
        const currentVars = new Set([...rowVariables, ...columnVariables]);
        
        requiredVars.forEach(requiredVar => {
          if (!currentVars.has(requiredVar)) {
            rowVariables.push(requiredVar);
          }
        });

        const { rows, cols } = getGridDimensions(rowVariables, columnVariables);
        const middleCell = rows > 0 ? getMiddleCell(rows, cols) : null;

        setTimeout(() => {
          set({
            target,
            rowVariables,
            columnVariables,
            selectedCell: middleCell,
            results: [],
            viewMode: 'snapshot' // Always reset to snapshot mode
          });

          if (middleCell) {
            setTimeout(() => {
              set(state => ({
                ...state,
                selectedCell: { ...middleCell }
              }));
            }, 0);
          }
        }, 150);
      },
      
      updateSetting: (key, value) => set((state) => ({
        settings: { ...state.settings, [key]: value },
        results: []
      })),
      
      updateValue: (type, value) => set((state) => ({
        values: {
          ...state.values,
          [type]: { ...state.values[type], value }
        },
        results: []
      })),
      
      updateStep: (type, step) => set((state) => ({
        values: {
          ...state.values,
          [type]: { ...state.values[type], step }
        },
        results: [],
        showToast: true,
        toastMessage: `${state.values[type].label} variable updated`
      })),
      
      // New action to update step direction
      updateStepDirection: (type, direction) => set((state) => ({
        values: {
          ...state.values,
          [type]: { ...state.values[type], stepDirection: direction }
        },
        results: [],
        showToast: true,
        toastMessage: `${state.values[type].label} variable updated`
      })),
      
      resetStepsToDefaults: () => set((state) => {
        const updatedValues = { ...state.values };
        
        // Reset each variable's step and direction to defaults
        Object.keys(updatedValues).forEach(key => {
          const varType = key as VariableType;
          updatedValues[varType] = {
            ...updatedValues[varType],
            step: DEFAULT_STEPS[varType],
            stepDirection: DEFAULT_VALUES[varType].stepDirection // Use default direction from DEFAULT_VALUES
          };
        });
        
        return {
          values: updatedValues,
          results: [],
          showToast: true,
          toastMessage: 'All variables reset to default'
        };
      }),
      
      addRowVariable: (type) => {
        const state = get();
        const newRowVariables = [...state.rowVariables, type];
        const { rows, cols } = getGridDimensions(newRowVariables, state.columnVariables);
        
        set({
          rowVariables: newRowVariables,
          selectedCell: getMiddleCell(rows, cols),
          results: [],
          // Only change viewMode if not already in power mode
          viewMode: state.viewMode === 'power' ? 'power' : (newRowVariables.length > 1 ? 'compare' : state.viewMode)
        });
      },
      
      removeRowVariable: (index) => {
        const state = get();
        const newRowVariables = state.rowVariables.filter((_, i) => i !== index);
        
        if (newRowVariables.length === 0) {
          set({
            rowVariables: [],
            columnVariables: [],
            selectedCell: null,
            results: [],
            viewMode: 'snapshot'
          });
          return;
        }
        
        const { rows, cols } = getGridDimensions(newRowVariables, state.columnVariables);
        
        set({
          rowVariables: newRowVariables,
          selectedCell: getMiddleCell(rows, cols),
          results: [],
          // Keep current viewMode regardless of variable count when in compare mode
          viewMode: state.viewMode === 'compare' ? 'compare' : 
                    (state.columnVariables.length > 0 ? 'power' : 
                    (newRowVariables.length > 1 ? 'compare' : 'snapshot'))
        });
      },
      
      addColumnVariable: (type) => {
        const state = get();
        const newColumnVariables = [...state.columnVariables, type];
        const { rows, cols } = getGridDimensions(state.rowVariables, newColumnVariables);
        
        set({
          columnVariables: newColumnVariables,
          selectedCell: getMiddleCell(rows, cols),
          results: [],
          // Always set to power mode when columns are present
          viewMode: 'power'
        });
      },
      
      removeColumnVariable: (index) => {
        const state = get();
        const newColumnVariables = state.columnVariables.filter((_, i) => i !== index);
        const { rows, cols } = getGridDimensions(state.rowVariables, newColumnVariables);
        
        set({
          columnVariables: newColumnVariables,
          selectedCell: getMiddleCell(rows, cols),
          results: [],
          // Maintain 'compare' mode if we were already in 'compare' mode and have no columns
          viewMode: newColumnVariables.length > 0 ? 'power' : 
                    (state.viewMode === 'compare' ? 'compare' : 
                    (state.rowVariables.length > 1 ? 'compare' : 'snapshot'))
        });
      },

      setSelectedCell: (cell) => set({ selectedCell: cell }),

      setResults: (results) => set({ results }),

      clearVariables: () => set({
        rowVariables: [],
        columnVariables: [],
        selectedCell: null,
        results: [],
        viewMode: 'snapshot',
      }),

      resetToDefaults: () => set({
        target: 'price',
        rowVariables: ['income', 'date'],
        columnVariables: [],
        values: {
          ...DEFAULT_VALUES,
          date: { ...DEFAULT_VALUES.date, value: nextMonthFirstDay }
        },
        settings: DEFAULT_SETTINGS,
        selectedCell: { row: 1, col: 0 },
        results: [],
        viewMode: 'snapshot',
        showToast: true,
        toastMessage: 'Reset to defaults'
      }),

      hideToast: () => set({ showToast: false, toastMessage: '' })
    }),
    {
      name: 'calculator-storage',
      version: STORE_VERSION,
      partialize: (state) => ({
        // Only persist values and settings, but not viewMode or target
        // This ensures we reset to price target and snapshot mode on page refresh
        values: state.values,
        settings: state.settings
      })
    }
  )
);